/**
 * 選択肢の札の1つ。hint は押せないときの理由など、札の下に小さく出す説明。
 * @graph-connects none
 */
export interface Choice<V extends string | boolean> {
  value: V;
  label: string;
  disabled?: boolean;
  hint?: string;
}

/**
 * 選択肢を札（チップ）で横に並べ、1つを選ぶ。選んだ札は色が付く。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の小窓や設定で、選択肢を札で並べて1つ選ばせる
 * @graph-connects none
 */
export function ChoiceChips<V extends string | boolean>({
  label,
  choices,
  value,
  onChange,
  disabled = false,
}: {
  label: string;
  choices: readonly Choice<V>[];
  value: V | null;
  onChange: (value: V) => void;
  disabled?: boolean;
}) {
  const hints = choices.flatMap((c) => (c.hint === undefined ? [] : [c.hint]));
  return (
    <div>
      <div role="group" aria-label={label} className="flex flex-wrap gap-2">
        {choices.map((choice) => {
          const selected = choice.value === value;
          return (
            <button
              key={String(choice.value)}
              type="button"
              aria-pressed={selected}
              disabled={disabled || choice.disabled === true}
              onClick={() => {
                onChange(choice.value);
              }}
              className={`rounded-lg border px-3 py-1.5 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                selected
                  ? 'border-[var(--color-accent-primary)] bg-[color-mix(in_srgb,var(--color-accent-primary)_10%,transparent)] font-semibold text-[var(--color-accent-primary)]'
                  : 'border-[var(--color-border)] bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] hover:border-[var(--color-border-hover)]'
              }`}
            >
              {choice.label}
            </button>
          );
        })}
      </div>
      {hints.map((hint) => (
        <p key={hint} className="mt-1 text-xs text-[var(--color-text-muted)]">
          {hint}
        </p>
      ))}
    </div>
  );
}
